import type { QuoteSummaryCommercialization } from '@/features/quotes/context/quote-summary.context';
import type { BusinessLocation } from '@/features/quotes/components/quote-steps/business-location';
import {
  isAssistanceCoverageCode,
  isCapitaCoverageCode,
} from '@/features/quotes/utils/map-business-risks-to-locations.util';
import type {
  Agent,
  AttachBusinessQuotePayload,
  CalculateAnalysisPayload,
  CoverageAttach,
  Customer,
  ProposalDetails,
  RiskQuestionary,
} from '@/services/quotes/types';

type FormSnapshot = Record<string, unknown>;
type ProposalRisk = NonNullable<
  NonNullable<ProposalDetails['lobs']>[number]['risks']
>[number];

const EPSILON = 0.0001;

function readString(values: FormSnapshot, key: string): string | undefined {
  const value = values[key];
  if (typeof value === 'string' && value.trim()) return value;
  return undefined;
}

function parsePercent(input: string | undefined): number | undefined {
  if (!input) return undefined;
  const n = parseFloat(String(input).replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

function parseCurrencyToNumber(currencyString: string | undefined): number {
  if (!currencyString || !currencyString.trim()) return 0;
  const cleaned = currencyString
    .replace(/R\$\s*/g, '')
    .replace(/\./g, '')
    .replace(',', '.')
    .trim();
  return parseFloat(cleaned) || 0;
}

function normalizeString(value: string | undefined | null): string {
  return (value ?? '').toString().trim();
}

function normalizeDigits(value: string | undefined | null): string {
  return normalizeString(value).replace(/\D/g, '');
}

function numbersClose(a: number, b: number): boolean {
  return Math.abs(a - b) < EPSILON;
}

// ============================================================================
// Customer diff
// ============================================================================

function diffCustomer(
  formValues: FormSnapshot,
  original: ProposalDetails['customer'] | undefined
): Partial<Customer> | undefined {
  const candidates: Array<{
    key: keyof Customer;
    current: string | undefined;
    originalValue: string | undefined;
  }> = [
    {
      key: 'name',
      current: readString(formValues, 'name'),
      originalValue: original?.name,
    },
    {
      key: 'registerNumber',
      current: readString(formValues, 'registerNumber'),
      // `idNo` no proposal do empresarial carrega o TipoDocumento ("2" = CNPJ),
      // não o número. O documento real vem em `cpf`/`documentNumber`/`NoDocumento`.
      originalValue: original?.cpf ?? original?.documentNumber ?? original?.idNo,
    },
    {
      key: 'email',
      current: readString(formValues, 'email'),
      originalValue: original?.email,
    },
    {
      key: 'cellphone',
      current: readString(formValues, 'phone'),
      originalValue: original?.cellphone,
    },
    {
      key: 'socialName',
      current: readString(formValues, 'socialName'),
      originalValue: original?.socialName,
    },
  ];

  const diff: Partial<Customer> = {};
  let changed = false;

  for (const { key, current, originalValue } of candidates) {
    if (current === undefined) continue;
    if (normalizeString(current) !== normalizeString(originalValue)) {
      (diff as Record<string, string>)[key] = current;
      changed = true;
    }
  }

  if (!changed) return undefined;

  // O DTO UpdateCustomerDto exige `name` e `registerNumber` como obrigatórios
  // (não-vazios) mesmo num customer "parcial". Garante que ambos vão no
  // payload — usa o valor atual do form ou cai pro original do proposal.
  if (diff.name === undefined) {
    const fallback = readString(formValues, 'name') ?? original?.name;
    if (fallback) diff.name = fallback;
  }
  if (diff.registerNumber === undefined) {
    const fallback =
      readString(formValues, 'registerNumber') ??
      original?.cpf ??
      original?.documentNumber ??
      original?.idNo;
    if (fallback) diff.registerNumber = fallback;
  }

  return diff;
}

// ============================================================================
// Agents diff
// ============================================================================

function diffAgents(
  commercialization: QuoteSummaryCommercialization,
  originalAgents: ProposalDetails['agents']
): Agent[] | undefined {
  const original = originalAgents?.[0];
  if (!original) return undefined;

  const currentName =
    commercialization.brokerName?.trim() || original.name?.trim() || '';
  const currentCommissionPercent = parsePercent(
    commercialization.commissionPercent
  );
  const currentSharePercent = parsePercent(
    commercialization.participationPercent
  );

  const originalCommissionPercent = (original.commissionRate ?? 0) * 100;
  const originalShareRate = original.shareRate ?? 1;
  const originalSharePercent = originalShareRate * 100;

  const nameChanged =
    normalizeString(currentName) !== normalizeString(original.name);
  const commissionChanged =
    currentCommissionPercent !== undefined &&
    !numbersClose(currentCommissionPercent, originalCommissionPercent);
  const shareChanged =
    currentSharePercent !== undefined &&
    !numbersClose(currentSharePercent, originalSharePercent);

  if (!nameChanged && !commissionChanged && !shareChanged) {
    return undefined;
  }

  return [
    {
      name: currentName,
      agentCode: original.agentCode ?? '',
      // BFF espera comissão em fração, como o `commissionRate` original
      // (10% → 0,1), não o percentual.
      commission:
        currentCommissionPercent !== undefined
          ? currentCommissionPercent / 100
          : (original.commissionRate ?? 0),
      shareRate:
        currentSharePercent !== undefined
          ? currentSharePercent / 100
          : originalShareRate,
    },
  ];
}

// ============================================================================
// Location helpers (montar payload completo de um local)
// ============================================================================

function buildRiskQuestionaryFromLocation(
  location: BusinessLocation
): RiskQuestionary | undefined {
  const [fireAnswer, upperFloorAnswer, insideMallAnswer, theftAnswer] =
    location.questionnaireAnswers ?? [];

  const toArray = (answer: string | string[] | undefined): string[] => {
    if (Array.isArray(answer)) return answer.filter(Boolean);
    if (answer) return [answer];
    return [];
  };
  const toString = (answer: string | string[] | undefined): string => {
    if (Array.isArray(answer)) return answer[0] ?? '';
    return answer ?? '';
  };

  const questionary: RiskQuestionary = {
    fireProtectionSystems: toArray(fireAnswer),
    isUpperFloor: toString(upperFloorAnswer),
    isInsideMall: toString(insideMallAnswer),
    securityAlarmSystems: toArray(theftAnswer),
  };

  const hasContent =
    (questionary.fireProtectionSystems?.length ?? 0) > 0 ||
    Boolean(questionary.isUpperFloor) ||
    Boolean(questionary.isInsideMall) ||
    (questionary.securityAlarmSystems?.length ?? 0) > 0;

  return hasContent ? questionary : undefined;
}

/**
 * Retorna apenas as coberturas alteradas em relação ao proposal original.
 * Compara `code` (productElementCode), `value` (sumInsured) e `codeDeductible`
 * por correspondência de código. Ignora ASSIST/CAPITA/planCode-tagged (igual
 * `normalizeProposalCoverages`).
 *
 * Retorna `undefined` se nenhuma cobertura mudou.
 */
function diffCoverages(
  location: BusinessLocation,
  risk: ProposalRisk
): CoverageAttach[] | undefined {
  const originalByCode = new Map<string, NormalizedCoverage>();
  for (const original of normalizeProposalCoverages(risk)) {
    originalByCode.set(original.code, original);
  }

  const changed: CoverageAttach[] = [];
  for (const coverage of location.coveragesPayload ?? []) {
    const code = normalizeString(coverage.code);
    if (!code) continue;
    const value = coverage.value ?? 0;
    if (value <= 0) continue;
    const codeDeductible = normalizeString(coverage.codeDeductible);

    const original = originalByCode.get(code);

    // Franquia personalizada ("Personalizar franquia") mantém o mesmo
    // `codeDeductible` base — a mudança aparece só no item estruturado
    // (`minValue`/`name`). Compara quando os dois lados têm o item.
    const structured = coverage.policyLimitDeductible?.[0];
    const originalStructured = original?.structuredDeductible;
    const structuredChanged =
      structured !== undefined &&
      originalStructured !== undefined &&
      (structured.minValue !== originalStructured.minValue ||
        normalizeString(structured.name) !==
          normalizeString(originalStructured.name));

    const isChanged =
      !original ||
      !numbersClose(original.value, value) ||
      original.codeDeductible !== codeDeductible ||
      structuredChanged;

    if (!isChanged) continue;

    changed.push({
      code: coverage.code,
      value: coverage.value,
      productElementId: coverage.productElementId,
      codeDeductible: coverage.codeDeductible,
      planCode: coverage.planCode,
      benefits: coverage.benefits?.map(benefit => ({
        code: benefit.code,
        value: benefit.value,
        productElementId: benefit.productElementId,
      })),
      ...(coverage.policyLimitDeductible?.length
        ? { policyLimitDeductible: coverage.policyLimitDeductible }
        : {}),
    });
  }

  return changed.length > 0 ? changed : undefined;
}

// ============================================================================
// Location diff (comparar local atual com o original do proposal)
// ============================================================================

type NormalizedCoverage = {
  code: string;
  value: number;
  codeDeductible: string;
  /**
   * Item estruturado de franquia persistido no proposal
   * (`policyLimitDeductible[0]`), quando existente — usado para detectar
   * personalização de franquia que mantém o mesmo `codeDeductible`.
   */
  structuredDeductible?: { minValue?: number; name?: string };
};

function normalizeProposalCoverages(
  risk: ProposalRisk
): NormalizedCoverage[] {
  return (risk.coverages ?? [])
    .filter(
      c =>
        !c.planCode &&
        !isAssistanceCoverageCode(c.productElementCode) &&
        !isCapitaCoverageCode(c.productElementCode)
    )
    .map(c => ({
      code: normalizeString(c.productElementCode),
      value: c.sumInsured ?? 0,
      codeDeductible:
        c.codeDeductible != null ? normalizeString(String(c.codeDeductible)) : '',
      ...(c.policyLimitDeductible?.[0]
        ? {
            structuredDeductible: {
              minValue: c.policyLimitDeductible[0].limitDeductibleValue,
              name: c.policyLimitDeductible[0].name,
            },
          }
        : {}),
    }))
    .filter(c => c.code && c.value > 0)
    .sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * Diff granular do `insuredObject`: compara cada campo da location atual com o
 * risk original do proposal e devolve só os que mudaram. Sempre inclui
 * `activityTypePerformed` quando há QUALQUER mudança (o BFF exige esse campo
 * para o empresarial sempre que `insuredObject` é enviado).
 *
 * Retorna `undefined` se nenhum campo mudou — sinaliza para o caller omitir
 * o `insuredObject` do payload.
 */
function diffInsuredObject(
  location: BusinessLocation,
  risk: ProposalRisk
): Partial<AttachBusinessQuotePayload['insuredObject']> | undefined {
  const diff: Partial<AttachBusinessQuotePayload['insuredObject']> = {};
  let changed = false;

  if (normalizeDigits(location.cep) !== normalizeDigits(risk.zipCode)) {
    diff.postCode = location.cep.replace(/\D/g, '');
    changed = true;
  }
  if (normalizeString(location.street) !== normalizeString(risk.address)) {
    diff.address = location.street;
    diff.street = location.street;
    changed = true;
  }
  if (normalizeString(location.number) !== normalizeString(risk.addressNumber)) {
    diff.addressNumber = location.number;
    changed = true;
  }
  if (
    normalizeString(location.neighborhood) !==
    normalizeString(risk.neighborhood)
  ) {
    diff.neighborhood = location.neighborhood;
    changed = true;
  }
  if (normalizeString(location.city) !== normalizeString(risk.city)) {
    diff.city = location.city;
    changed = true;
  }
  if (normalizeString(location.state) !== normalizeString(risk.state)) {
    diff.state = location.state;
    changed = true;
  }
  if (
    normalizeString(location.constructionType) !==
    normalizeString(risk.constructionType)
  ) {
    diff.constructionType = location.constructionType;
    changed = true;
  }
  if (
    normalizeString(location.propertyType ?? '') !==
    normalizeString(risk.propertyType)
  ) {
    diff.propertyType = location.propertyType ?? '1';
    changed = true;
  }
  if (
    normalizeString(location.propertyClassification ?? '') !==
    normalizeString(risk.propertyClassification)
  ) {
    diff.propertyClassification = location.propertyClassification ?? '1';
    changed = true;
  }

  const originalScheduleExt = risk.scheduleExt?.[0];
  const originalValue = originalScheduleExt?.value ?? 0;
  const currentValue = parseCurrencyToNumber(location.riskValue);
  const scheduleValueChanged = !numbersClose(currentValue, originalValue);
  const scheduleObjectChanged =
    normalizeString(location.insuredObjectType) !==
    normalizeString(originalScheduleExt?.insuredObject);
  if (scheduleValueChanged || scheduleObjectChanged) {
    diff.scheduleExt = [
      {
        insuredObject: location.insuredObjectType,
        value: currentValue,
      },
    ];
    changed = true;
  }

  const originalBusinessInterruption = risk.businessInterruptionAmount ?? 0;
  const currentBusinessInterruption = parseCurrencyToNumber(
    location.businessInterruptionValue ?? ''
  );
  if (
    !numbersClose(currentBusinessInterruption, originalBusinessInterruption)
  ) {
    diff.businessInterruptionAmount = currentBusinessInterruption;
    changed = true;
  }

  const originalUnderwritingDiscount =
    risk.underwritingDiscountRequestInd === 'Y' ? 'Y' : 'N';
  const currentUnderwritingDiscount =
    location.underwritingDiscountRequestInd === 'Y' ? 'Y' : 'N';
  if (currentUnderwritingDiscount !== originalUnderwritingDiscount) {
    diff.underwritingDiscountRequestInd = currentUnderwritingDiscount;
    changed = true;
  }

  if (!changed) return undefined;

  // BFF empresarial exige `activityTypePerformed` sempre que `insuredObject`
  // é enviado — cai no valor atual da location.
  diff.activityTypePerformed = location.activityType;

  return diff;
}

/**
 * Diff do `riskQuestionary` para o primeiro local. Como os campos do
 * questionário não estão no tipo de `ProposalRisk` (vêm cru do InsureMO),
 * acessamos via cast e comparamos com as respostas montadas pela location atual.
 *
 * Retorna `undefined` se nenhum campo mudou.
 */
function diffRiskQuestionary(
  location: BusinessLocation,
  risk: ProposalRisk
): RiskQuestionary | undefined {
  const current = buildRiskQuestionaryFromLocation(location);
  if (!current) return undefined;

  const raw = risk as Record<string, unknown>;
  const toList = (value: unknown): string[] => {
    if (Array.isArray(value)) return value.map(v => normalizeString(String(v)));
    if (value != null && value !== '') return [normalizeString(String(value))];
    return [];
  };
  const originalFire = toList(raw.fireProtectionSystems);
  const originalTheft = toList(raw.securityAlarmSystems);
  const currentFire = (current.fireProtectionSystems ?? []).map(normalizeString);
  const currentTheft = (current.securityAlarmSystems ?? []).map(normalizeString);

  const sameList = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, idx) => value === sortedB[idx]);
  };

  const fireChanged = !sameList(currentFire, originalFire);
  const theftChanged = !sameList(currentTheft, originalTheft);
  const upperChanged =
    normalizeString(current.isUpperFloor) !==
    normalizeString(raw.isUpperFloor as string | undefined);
  const mallChanged =
    normalizeString(current.isInsideMall) !==
    normalizeString(raw.isInsideMall as string | undefined);

  if (!fireChanged && !theftChanged && !upperChanged && !mallChanged) {
    return undefined;
  }

  return current;
}

// ============================================================================
// Entry point
// ============================================================================

/**
 * Monta o payload do POST /portal/v1/business/quotes/:policyId/calculate-analysis
 * comparando o estado atual da tela de análise com o `proposal` original e
 * incluindo apenas os campos efetivamente alterados.
 *
 * Tudo é achatado no topo (o BFF não aceita `locations[]`). Para o empresarial
 * atual, só existe um risco por cotação — pegamos o primeiro local com mudança
 * e desfazemos em `insuredObject` / `coverages` / `riskQuestionary` parciais.
 *
 * Escolha do `step`:
 *  - apenas customer alterado → `CLIENT_INFO`
 *  - apenas coverages alterado → `COVERAGES` + `validationEventCode: ValidationQuotationCoverages`
 *  - tudo o mais (agents, insuredObject, questionary, combinações) → `CALCULATION`
 */
export function buildAnalysisPayload(
  proposal: ProposalDetails,
  formValues: FormSnapshot,
  commercialization: QuoteSummaryCommercialization,
  locations: BusinessLocation[] = []
): CalculateAnalysisPayload {
  const customer = diffCustomer(formValues, proposal.customer);
  const agents = diffAgents(commercialization, proposal.agents);

  const firstRisk = proposal.lobs?.[0]?.risks?.[0];
  const firstLocation = locations[0];

  const insuredObject =
    firstLocation && firstRisk
      ? diffInsuredObject(firstLocation, firstRisk)
      : undefined;
  const coverages =
    firstLocation && firstRisk
      ? diffCoverages(firstLocation, firstRisk)
      : undefined;
  const riskQuestionary =
    firstLocation && firstRisk
      ? diffRiskQuestionary(firstLocation, firstRisk)
      : undefined;

  const onlyCustomer =
    !!customer && !agents && !insuredObject && !coverages && !riskQuestionary;

  if (onlyCustomer) {
    return { step: 'CLIENT_INFO', customer };
  }

  // O BFF empresarial exige `insuredObject` no mesmo payload sempre que
  // mandar `coverages`/`benefits`/`riskQuestionary` (pra saber qual risco
  // alvejar). Quando nenhum campo do insuredObject mudou mas precisamos
  // enviar coverages/questionary, montamos um insuredObject mínimo só com o
  // `activityTypePerformed` (sempre obrigatório no business).
  const needsInsuredObjectForRiskContext =
    (!!coverages || !!riskQuestionary) && !insuredObject;
  const effectiveInsuredObject = needsInsuredObjectForRiskContext && firstLocation
    ? { activityTypePerformed: firstLocation.activityType }
    : insuredObject;

  return {
    step: 'CALCULATION',
    ...(customer ? { customer } : {}),
    ...(agents ? { agents } : {}),
    ...(effectiveInsuredObject ? { insuredObject: effectiveInsuredObject } : {}),
    ...(coverages ? { coverages } : {}),
    ...(riskQuestionary ? { riskQuestionary } : {}),
  };
}

/**
 * Retorna `true` quando o payload carrega ao menos uma alteração efetiva
 * (algum campo além do `step` obrigatório).
 */
export function payloadHasChanges(payload: CalculateAnalysisPayload): boolean {
  return (
    payload.customer !== undefined ||
    payload.agents !== undefined ||
    payload.coverages !== undefined ||
    payload.benefits !== undefined ||
    payload.insuredObject !== undefined
  );
}
